import { FC } from 'react';
import { IconButton, Avatar, Toolbar, AppBar, Typography, Box } from '@mui/material';

import MenuIcon from '@mui/icons-material/Menu';
import { useConfig } from '../components/Configuration';
import { useAuth } from 'oidc-react';
import { IdTokenClaims } from 'oidc-client-ts';
import gravatarUrl from 'gravatar-url';

const drawerWidth = 240;

interface TopHeaderProps {
  handleDrawerToggle(): void;
}

export const TopHeader: FC<TopHeaderProps> = ({ handleDrawerToggle }) => {
  const auth = useAuth();
  const config = useConfig();
  const initials = getInitials(auth.userData?.profile);
  const email = auth.userData?.profile?.email;

  const url = email ? gravatarUrl(email, { size: 200, default: '404' }) : undefined;

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}>
        <Toolbar>
          <IconButton color="inherit" aria-label="open drawer" onClick={handleDrawerToggle} sx={{ mr: 2, display: { sm: 'none' } }} size="large">
            <MenuIcon />
          </IconButton>
          <Typography
            variant="h6"
            noWrap
            sx={{
              color: "inherit",
              flex: 1
            }}>
            Salg
          </Typography>
          <Avatar src={url}>{initials}</Avatar>
        </Toolbar>
      </AppBar>
      {config && <EnvironmentRibbon environment={config.env} />}
    </>
  );
};

function EnvironmentRibbon({ environment }: { environment: string }) {
  const ribbon = getRibbon(environment);
  if (!ribbon) return null;

  const title = `Salg - ${ribbon.label}`;
  return (
    <>
      <title>{title}</title>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          right: 0,
          width: 150,
          height: 150,
          overflow: 'hidden',
          pointerEvents: 'none',
          zIndex: (theme) => theme.zIndex.appBar + 1,
        }}>
        <Box
          sx={{
            position: 'absolute',
            top: 36,
            right: -55,
            width: 225,
            py: '6px',
            backgroundColor: ribbon.color,
            color: 'common.white',
            textAlign: 'center',
            fontWeight: 700,
            fontSize: 12,
            letterSpacing: 1.5,
            boxShadow: '0 3px 10px rgba(0,0,0,0.3)',
            transform: 'rotate(45deg)',
            textTransform: 'uppercase',
          }}>
          {ribbon.label}
        </Box>
      </Box>
    </>
  );
}

function getRibbon(environment: string): { label: string; color: string } | null {
  if (environment === 'Development') return { label: 'DEV', color: '#2e7d32' };
  if (environment === 'test') return { label: 'TEST', color: '#ed6c02' };
  if (environment === 'staging') return { label: 'BETA', color: '#1976d2' };
  return null;
}

const getInitials = (profile?: IdTokenClaims) => {
  if (!profile || !profile.given_name?.length || !profile.family_name?.length) return '';
  return `${profile.given_name[0].toUpperCase()}${profile.family_name[0].toUpperCase()}`;
};
